import { useState } from 'react';
import { API_BASE_URL } from '../utils/constants';

const API_BASE = '/api';

// Make request with credentials
const makeRequest = async (url, options = {}) => {
  const config = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    credentials: 'include'
  };

  return fetch(url, config);
};

export const useSalesSearch = () => {
  const [salesResults, setSalesResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(null);

  // Search sales history with filters
  const searchSales = async (filters, offset = 0) => {
    if (offset === 0) {
      setLoading(true);
      setSalesResults([]);
      setTotalCount(null);
    }
    setError('');

    try {
      const params = new URLSearchParams();

      if (filters.minSalePrice) {
        params.append('minPrice', filters.minSalePrice);
      }
      if (filters.maxSalePrice) {
        params.append('maxPrice', filters.maxSalePrice);
      }
      if (filters.minSaleYear) {
        params.append('minYear', filters.minSaleYear);
      }
      if (filters.maxSaleYear) {
        params.append('maxYear', filters.maxSaleYear);
      }
      if (filters.salesBuildingType) {
        params.append('buildingType', filters.salesBuildingType);
      }
      if (filters.salesAddress) {
        params.append('address', filters.salesAddress);
      }

      if (offset > 0) {
        params.append('offset', offset);
      }

      const queryString = params.toString();
      const url = `${API_BASE}/sales/search${queryString ? `?${queryString}` : ''}`;

      const response = await makeRequest(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setSalesResults(data.results || []);
      setTotalCount(data.totalCount || 0);

      return data;
    } catch (err) {
      console.error('Error searching sales:', err);
      setError('Failed to search sales history. Please try again.');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Fetch full property details from Winnipeg API using roll numbers
  const fetchPropertiesFromRollNumbers = async (rollNumbers) => {
    if (!rollNumbers || rollNumbers.length === 0) {
      return [];
    }

    try {
      // Build the query to fetch all properties with these roll numbers
      // Use SoQL IN clause for efficiency
      const rollNumberList = rollNumbers.map(rn => `'${rn}'`).join(',');
      const whereClause = `roll_number IN (${rollNumberList})`;
      const query = `${API_BASE_URL}?$where=${encodeURIComponent(whereClause)}&$limit=${rollNumbers.length}`;

      const response = await fetch(query);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const properties = await response.json();
      return properties;
    } catch (err) {
      console.error('Error fetching properties from roll numbers:', err);
      return [];
    }
  };

  // Clear search results
  const clearResults = () => {
    setSalesResults([]);
    setTotalCount(null);
    setError('');
  };

  return {
    salesResults,
    loading,
    error,
    totalCount,
    searchSales,
    fetchPropertiesFromRollNumbers,
    clearResults
  };
};
